// src/services/liveScores.js
// Cricbuzz-compatible live score polling via RapidAPI.
// Falls back gracefully if API is unavailable.

// ── Config (set your key in .env as REACT_APP_CRICKET_API_KEY) ──
// Browser-stored keys are written to window.__CRICKET_API_KEY__ at runtime.
const BASE_URL = "https://cricbuzz-cricket.p.rapidapi.com";
const POLL_MS  = 30_000; // poll every 30s during live matches

// ── Fetch helpers ───────────────────────────────────────────────
function getApiKey() {
  return process.env.REACT_APP_CRICKET_API_KEY || (typeof window !== "undefined" && window.__CRICKET_API_KEY__) || "";
}

async function apiFetch(path) {
  const key = getApiKey();
  if (!key) throw new Error("No API key set (REACT_APP_CRICKET_API_KEY)");
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: {
      "x-rapidapi-key":  key,
      "x-rapidapi-host": "cricbuzz-cricket.p.rapidapi.com",
    },
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

// ── Helper: extract IPL matches from a typeMatches response ───
function extractIplMatches(data, matchState) {
  const all = data?.typeMatches || [];
  const ipl = [];
  for (const type of all) {
    for (const series of type.seriesMatches || []) {
      const wrapper = series?.seriesAdWrapper;
      const info = wrapper?.seriesName || "";
      if (info.toLowerCase().includes("ipl") || info.toLowerCase().includes("indian premier")) {
        // Pull seriesId from wrapper (more reliable than matchInfo.seriesId)
        const seriesId = wrapper?.seriesId;
        for (const m of wrapper?.matches || []) {
          ipl.push({ ...m.matchInfo, seriesId: seriesId ?? m.matchInfo?.seriesId, matchState });
        }
      }
    }
  }
  return ipl;
}

// ── Get live IPL matches ───────────────────────────────────────
export async function fetchLiveMatches() {
  const data = await apiFetch("/matches/v1/live");
  return extractIplMatches(data, "live");
}

// ── Get recently completed IPL matches ────────────────────────
export async function fetchRecentMatches() {
  const data = await apiFetch("/matches/v1/recent");
  return extractIplMatches(data, "recent");
}

// ── Get ALL IPL matches available from the API ─────────────────
// For an ongoing tournament, Cricbuzz's live + recent endpoints
// typically return every match in the current series, not just
// the past few days.  Fetch both and deduplicate by matchId.
export async function fetchAllIplMatches() {
  const [liveFetch, recentFetch] = await Promise.allSettled([
    fetchLiveMatches(),
    fetchRecentMatches(),
  ]);
  const live   = liveFetch.status   === "fulfilled" ? liveFetch.value   : [];
  const recent = recentFetch.status === "fulfilled" ? recentFetch.value : [];
  // Deduplicate: live takes precedence over recent for the same matchId
  const seen = new Set();
  const all = [];
  for (const m of [...live, ...recent]) {
    if (m.matchId && !seen.has(m.matchId)) { seen.add(m.matchId); all.push(m); }
  }
  return all;
}
export async function fetchScorecard(matchId) {
  return apiFetch(`/mcenter/v1/${matchId}/scard`);
}

// ── Parse scorecard into per-player stat lines ─────────────────
// Returns { [playerName]: { bat, bowl, fld } }
//
// Cricbuzz RapidAPI actual shape (confirmed from live response):
//   { scorecard: [ { inningsid, batsman: [...], bowler: [...] } ] }
// Legacy shape also handled:
//   { scoreCard: [ { batTeamDetails: { batsmenData: {...} }, bowlTeamDetails: ... } ] }
export function parseScorecardToStats(scorecard) {
  const stats = {};

  // Resolve innings array — try both key spellings
  let innings = [];
  if      (Array.isArray(scorecard?.scorecard)) innings = scorecard.scorecard; // actual API
  else if (Array.isArray(scorecard?.scoreCard)) innings = scorecard.scoreCard; // legacy
  else if (Array.isArray(scorecard))            innings = scorecard;

  const ensure = (name) => {
    if (!name) return null;
    const key = name.trim();
    if (!stats[key]) {
      stats[key] = {
        bat:  { runs: 0, balls: 0, fours: 0, sixes: 0, out: false },
        bowl: { overs: 0, runs: 0, wkts: 0, maidens: 0, dots: 0 },
        fld:  { catches: 0, stumpings: 0, runoutDirect: 0, runoutAssist: 0 },
      };
    }
    return key;
  };

  for (const inning of innings) {
    // ── Batting ──────────────────────────────────────────────────
    // Actual API: inning.batsman = array
    // Legacy API: inning.batTeamDetails.batsmenData = object
    const batsmen = Array.isArray(inning?.batsman)
      ? inning.batsman
      : Object.values(inning?.batTeamDetails?.batsmenData || inning?.batTeamDetails?.batsmanData || {});

    for (const batter of batsmen) {
      // Actual API: 'name'  |  legacy: 'batName'
      const name = batter?.name || batter?.batName;
      const k = ensure(name);
      if (!k) continue;
      stats[k].bat.runs  = batter.runs  ?? 0;
      stats[k].bat.balls = batter.balls ?? 0;
      stats[k].bat.fours = batter.fours ?? 0;
      stats[k].bat.sixes = batter.sixes ?? 0;
      // Actual API: 'outdec'  |  legacy: 'outDesc'
      const howOut = (batter.outdec || batter.outDesc || "").toLowerCase();
      stats[k].bat.out = !!howOut && !howOut.includes("not out") && !howOut.includes("batting") && !howOut.includes("dnb");

      if (howOut.includes("c ") || howOut.startsWith("caught")) {
        // "c & b {bowler}" = caught-and-bowled: fielder IS the bowler
        if (howOut.startsWith("c & b ") || howOut.startsWith("c and b ")) {
          const bowlerName = howOut.replace(/^c\s*(?:&|and)\s*b\s+/, "").trim();
          const fk = ensure(bowlerName);
          if (fk) stats[fk].fld.catches++;
        } else {
          const fielder = howOut.replace(/^c\s+/, "").replace(/\s+b\s+\w.*/, "").trim();
          const fk = ensure(fielder);
          if (fk) stats[fk].fld.catches++;
        }
      }
      if (howOut.includes("st ")) {
        const fielder = howOut.replace(/^st\s+/, "").replace(/\s+b\s+\w.*/, "").trim();
        const fk = ensure(fielder);
        if (fk) stats[fk].fld.stumpings++;
      }
      if (howOut.includes("run out")) {
        const fielder = howOut.replace(/run out\s*\(/, "").replace(/\)/, "").trim();
        if (fielder.includes("/")) {
          const [a, b2] = fielder.split("/").map(s => s.trim());
          const fka = ensure(a), fkb = ensure(b2);
          if (fka) stats[fka].fld.runoutDirect++;
          if (fkb) stats[fkb].fld.runoutAssist++;
        } else {
          const fk = ensure(fielder);
          if (fk) stats[fk].fld.runoutDirect++;
        }
      }
    }

    // ── Bowling ──────────────────────────────────────────────────
    // Actual API: inning.bowler = array  |  legacy: bowlTeamDetails.bowlersData
    const bowlers = Array.isArray(inning?.bowler)
      ? inning.bowler
      : Object.values(inning?.bowlTeamDetails?.bowlersData || inning?.bowlTeamDetails?.bowlerData || {});

    for (const bowler of bowlers) {
      const name = bowler?.name || bowler?.bowlName;
      const k = ensure(name);
      if (!k) continue;
      stats[k].bowl.overs   = parseFloat(bowler.overs)   || 0;
      stats[k].bowl.runs    = bowler.runs    ?? 0;
      stats[k].bowl.wkts    = bowler.wickets ?? 0;
      stats[k].bowl.maidens = bowler.maidens ?? 0;
      stats[k].bowl.dots    = bowler.dots    ?? 0;
    }
  }

  return stats;
}

// ── Fuzzy name match: API names vs our player list ─────────────
// Handles "V Kohli", "Virat Kohli", "MS Dhoni", "KL Rahul", etc.
export function matchPlayerName(apiName, ourPlayers) {
  if (!apiName || !ourPlayers?.length) return null;
  const api = apiName.toLowerCase().trim();

  // 1. Exact match
  const exact = ourPlayers.find(p => p.name.toLowerCase() === api);
  if (exact) return exact;

  const parts = api.split(/\s+/);
  const apiLast = parts[parts.length - 1];

  // 2. Last name only (unique)
  const lastMatches = ourPlayers.filter(p => p.name.toLowerCase().split(/\s+/).pop() === apiLast);
  if (lastMatches.length === 1) return lastMatches[0];

  // 3. Initial(s) + last name: "V Kohli", "KL Rahul", "MS Dhoni"
  if (parts.length >= 2) {
    const initials = parts.slice(0, -1).map(s => s.replace(/\./g, ""));
    const init = ourPlayers.find(p => {
      const n = p.name.toLowerCase().split(/\s+/);
      if (n[n.length - 1] !== apiLast) return false;
      // Every supplied initial must match the start of the corresponding word
      return initials.every((ini, i) => i < n.length - 1 && n[i].startsWith(ini));
    });
    if (init) return init;
  }

  // 4. API has full first name, our list has initial (inverse of above)
  // e.g. API: "Jasprit Bumrah", ours: "J Bumrah" — unlikely but handle it
  if (parts.length >= 2) {
    const apiFirst = parts[0];
    const inv = ourPlayers.find(p => {
      const n = p.name.toLowerCase().split(/\s+/);
      if (n[n.length - 1] !== apiLast) return false;
      return apiFirst.startsWith(n[0].replace(/\./g, ""));
    });
    if (inv) return inv;
  }

  // 5. Partial last name (e.g. "de Villiers" vs "De Villiers") — case insensitive
  const lastCI = ourPlayers.filter(p => p.name.toLowerCase().endsWith(apiLast));
  if (lastCI.length === 1) return lastCI[0];

  return null;
}

// ── Poller class ───────────────────────────────────────────────
export class LiveScorePoller {
  constructor({ onStats, onError, onMatchList }) {
    this.onStats     = onStats;
    this.onError     = onError;
    this.onMatchList = onMatchList;
    this._timer      = null;
    this._matchId    = null;
    this._running    = false;
  }

  setMatch(matchId) {
    this._matchId = matchId;
    if (this._running) this._poll();
  }

  start() {
    this._running = true;
    this._poll();
    this._timer = setInterval(() => this._poll(), POLL_MS);
  }

  stop() {
    this._running = false;
    if (this._timer) { clearInterval(this._timer); this._timer = null; }
  }

  async _poll() {
    try {
      // Always refresh match list — combine live + recently completed
      const [liveFetch, recentFetch] = await Promise.allSettled([
        fetchLiveMatches(),
        fetchRecentMatches(),
      ]);
      const live   = liveFetch.status   === "fulfilled" ? liveFetch.value   : [];
      const recent = recentFetch.status === "fulfilled" ? recentFetch.value : [];
      if (this.onMatchList) this.onMatchList([...live, ...recent]);

      if (!this._matchId) return;
      const sc = await fetchScorecard(this._matchId);
      const parsed = parseScorecardToStats(sc);
      if (this.onStats) this.onStats(parsed, sc);
    } catch (e) {
      if (this.onError) this.onError(e);
    }
  }
}
